import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, PencilLine, Building2, CircleX } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiCallError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Field, FieldGroup, FieldLabel, FieldDescription } from '@/components/ui/field';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MarangatuSnapshotView, type Snapshot } from '@/components/marangatu-snapshot-view';

const ERROR_LABEL: Record<string, string> = {
  access_not_authorized: 'El titular no autorizó el acceso a su cuenta.',
  no_credentials: 'No hay credenciales de Marangatu cargadas.',
  login_failed: 'No pudimos loguear (¿credenciales mal o cambió SET?).',
  login_offsite: 'El login redirigió fuera de SET — abortado.',
  sync_failed: 'Falló el scrape. Revisá o cargá los datos manualmente.',
};

export function MarangatuSnapshotCard({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const base = `/api/admin/tenants/${tenantId}/set-access`;
  const [manualOpen, setManualOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['mar-snapshot', tenantId],
    queryFn: () => api<{ snapshot: Snapshot | null }>(`${base}/snapshot`),
  });
  const snap = data?.snapshot ?? null;

  const sync = useMutation({
    mutationFn: () => api<{ ok: boolean; error?: string }>(`${base}/sync`, { method: 'POST' }),
    onSuccess: (r) => {
      if (r.ok) toast.success('Datos sincronizados desde Marangatu');
      else toast.error(ERROR_LABEL[r.error ?? ''] ?? 'No se pudo sincronizar');
      qc.invalidateQueries({ queryKey: ['mar-snapshot', tenantId] });
    },
    onError: () => toast.error('No se pudo sincronizar'),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="size-4 text-muted-foreground" />
            Estado de cuenta SET
            {snap?.source === 'manual' && (
              <Badge variant="secondary" className="text-[10px]">
                carga manual
              </Badge>
            )}
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={sync.isPending} onClick={() => sync.mutate()}>
              <RefreshCw className={sync.isPending ? 'animate-spin' : ''} />
              {sync.isPending ? 'Sincronizando…' : 'Sincronizar ahora'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setManualOpen(true)}>
              <PencilLine /> Cargar manual
            </Button>
          </div>
        </div>
        <CardDescription>
          {snap?.scrapedAt
            ? `Última actualización: ${new Date(snap.scrapedAt).toLocaleString('es-PY')}`
            : 'Sin datos todavía — sincronizá o cargá manualmente.'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : !snap ? (
          <p className="text-sm text-muted-foreground">
            Aún no extrajimos datos de la cuenta de este contribuyente.
          </p>
        ) : snap.status === 'error' ? (
          <p className="flex items-start gap-2 text-sm text-destructive">
            <CircleX className="mt-0.5 size-4 shrink-0" />
            {ERROR_LABEL[snap.error ?? ''] ?? `No se pudo sincronizar (${snap.error ?? 'error'}).`}
          </p>
        ) : (
          <MarangatuSnapshotView snapshot={snap} />
        )}
      </CardContent>

      <ManualSnapshotDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        base={base}
        initial={snap}
        onSaved={() => {
          setManualOpen(false);
          qc.invalidateQueries({ queryKey: ['mar-snapshot', tenantId] });
        }}
      />
    </Card>
  );
}

function ManualSnapshotDialog({
  open,
  onClose,
  base,
  initial,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  base: string;
  initial: Snapshot | null;
  onSaved: () => void;
}) {
  const [estado, setEstado] = useState(initial?.estado ?? '');
  const [saldo, setSaldo] = useState(initial?.saldoFavor ?? '');

  const save = useMutation({
    mutationFn: () =>
      api(`${base}/snapshot`, {
        method: 'PUT',
        body: JSON.stringify({
          estado: estado.trim() || null,
          saldoFavor: saldo.trim() ? Number(saldo.replace(/[^\d.-]/g, '')) : null,
        }),
      }),
    onSuccess: () => {
      toast.success('Datos cargados manualmente');
      onSaved();
    },
    onError: (e) =>
      toast.error(e instanceof ApiCallError ? (e.body.message ?? e.body.error) : 'No se pudo guardar'),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cargar datos manualmente</DialogTitle>
          <DialogDescription>
            Fallback para cuando el scrape se rompe: entrá vos a Marangatu y cargá los datos a mano.
            Se marca como “carga manual”.
          </DialogDescription>
        </DialogHeader>
        <FieldGroup className="py-2">
          <Field>
            <FieldLabel htmlFor="ms-estado">Estado del contribuyente</FieldLabel>
            <Input id="ms-estado" value={estado} onChange={(e) => setEstado(e.target.value)} placeholder="ACTIVO" />
          </Field>
          <Field>
            <FieldLabel htmlFor="ms-saldo">Saldo a favor (Gs)</FieldLabel>
            <Input
              id="ms-saldo"
              inputMode="numeric"
              value={saldo}
              onChange={(e) => setSaldo(e.target.value)}
              placeholder="14142585"
            />
            <FieldDescription>Solo el monto, sin separadores.</FieldDescription>
          </Field>
        </FieldGroup>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
